import type {Account, UiSettings} from '@myparcel-frontend/types';
import useAccount from '@/composables/useAccount/useAccount';
import useAcl from '@/composables/useAcl';
import useSubscription from '@/composables/useSubscription';
import useSubscriptionCapabilities from '@/composables/useSubscriptionCapabilities';
import useUiSettings from '@/composables/useUiSettings';
import {useConfigStore} from '@/store/config';
import logToMonitor from '@/utils/logging';

export const loadAccountData = async (): Promise<{
  uiSettings: UiSettings | undefined;
  account: Account | undefined;
}> => {
  const {hasPermission} = useAcl();
  const {getUiSettings, ensurePreferredShop} = useUiSettings();
  const {getAccount} = useAccount();
  const configStore = useConfigStore();

  const [uiSettings, account] = await Promise.allSettled([getUiSettings(), getAccount()]);

  if (hasPermission('selectSubscription')) {
    const {getSubscriptions} = useSubscription();
    const {getSubscriptionCapabilities} = useSubscriptionCapabilities();

    const [subscriptions, subscriptionCapabilities] = await Promise.allSettled([
      getSubscriptions(),
      getSubscriptionCapabilities(),
    ]);

    if (subscriptions.status === 'rejected') {
      configStore.accountFetchError = true;
      logToMonitor(subscriptions.reason, {
        _myparcel_extra_info: 'Loading subscriptions failed during account bootstrap',
      });
    }

    if (subscriptionCapabilities.status === 'rejected') {
      configStore.accountFetchError = true;
      logToMonitor(subscriptionCapabilities.reason, {
        _myparcel_extra_info: 'Loading subscription capabilities failed during account bootstrap',
      });
    }
  }

  if (uiSettings.status === 'rejected') {
    configStore.accountFetchError = true;
    logToMonitor(uiSettings.reason, {
      _myparcel_extra_info: 'Loading UI settings failed during account bootstrap',
    });
  }

  if (account.status === 'rejected') {
    configStore.accountFetchError = true;
    logToMonitor(account.reason, {
      _myparcel_extra_info: 'Loading account data failed during account bootstrap',
    });
  }

  // preferred_shop_id might be missing in some scenarios. If so, we set it to the first shop of the account,
  // and put it in the API and store. This can only be done after both account and uiSettings are fulfilled,
  // because we need the shops from the account, and the uiSettings from the API to update it.
  let repairedUiSettings: UiSettings | undefined = uiSettings.status === 'fulfilled' ? uiSettings.value : undefined;

  if (
    account.status === 'fulfilled' &&
    uiSettings.status === 'fulfilled' &&
    uiSettings.value &&
    !uiSettings.value.preferred_shop_id
  ) {
    repairedUiSettings = await ensurePreferredShop(uiSettings.value, account.value.shops);
  }

  return {
    uiSettings: repairedUiSettings,
    account: account.status === 'fulfilled' ? account.value : undefined,
  };
};
